import { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';
import { ModalGeneric, SelectGeneric } from 'ypf-pas-general-ui-components';

import DateRange from '../AddPLToUbicationModal/DateRange';
import BoxesWithText from '../../BoxesWithText/BoxesWithText';
import useToast from '../../../../hooks/useToast';
import {
  formatFullDate,
  formatsEnum,
  getFirstDayOfMonth,
  getLastDateOfMonth,
  MONTHS
} from '../../../../utils/formatUtils';
import {
  postUbicationToPosLine,
  getExistingUbicationsOfSSMonthly
} from '../../../../services/monthlyServiceRequest';
import LoaderContext from '../../../../context/LoaderProvider';

const formatDateRange = (dateRange) => ({
  fechaInicio: dateRange.startDate,
  fechaFinaliza: dateRange.finalDate || dateRange.startDate
});

const formatLinesBody = (dateRanges, posLines, applyTemplate = false) => {
  let dateRangesByPosLine = [];

  if (applyTemplate) {
    dateRangesByPosLine = posLines.map((line) => ({
      ...line,
      rangoFechas: []
    }));
  } else {
    const posLineIds = posLines.map((posLine) => posLine.id);

    dateRangesByPosLine = posLineIds.map((idLinea) => ({
      idLinea,
      rangoFechas: []
    }));
  }

  dateRanges.forEach((dateRange) => {
    const index = dateRangesByPosLine.findIndex(
      (posLineDateRange) =>
        (posLineDateRange.idLinea || posLineDateRange.id) === dateRange.posLineId
    );

    dateRangesByPosLine[index].rangoFechas.push(formatDateRange(dateRange));
    dateRangesByPosLine[index].uMedida = dateRangesByPosLine[index].unidadMedida;
    delete dateRangesByPosLine[index].unidadMedida;
  });

  return dateRangesByPosLine;
};

const AddUbicationsToPL = ({
  idServisRequestMonthly,
  initialUbicationsSelecteds,
  month,
  onSubmit,
  positionsLinesSelecteds,
  setShowAddUbicationsToPLModal,
  showAddUbicationsToPLModal,
  year
}) => {
  const [ubications, setUbications] = useState([]);
  const [ubicationsSelecteds, setUbicationsSelecteds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRanges, setDateRanges] = useState([]);
  const [positionLinesSelectedsState, setPositionLinesSelectedsState] = useState([]);
  const [disabledPrimaryAction, setDisabledPrimaryAction] = useState(false);
  const { setLoader } = useContext(LoaderContext);
  const { id } = useParams();

  const firstDayOfSSM = getFirstDayOfMonth(MONTHS.indexOf(month), year);
  const lastDayOfSSM = getLastDateOfMonth(MONTHS.indexOf(month), year);
  const { setNewToast } = useToast();

  // Logic of get ubications when we select a position
  const getUbications = useCallback(
    async (positionsLines) => {
      try {
        setLoading(true);
        const responseubications = await getExistingUbicationsOfSSMonthly(idServisRequestMonthly);

        setUbications(responseubications);
        setPositionLinesSelectedsState(positionsLines);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        setNewToast({
          type: 'error',
          message: err.message
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [idServisRequestMonthly, setNewToast]
  );

  const handleOnChangeUbication = (ubicationSelected) => {
    // Push Ubication selected
    ubicationsSelecteds.push(ubicationSelected);
    setUbicationsSelecteds(ubicationSelected);
  };

  const handleChangeStartDate = (value, index) => {
    const newDateRanges = [...dateRanges];

    newDateRanges[index] = {
      ...newDateRanges[index],
      startDate: value,
      finalDate: ''
    };
    setDateRanges(newDateRanges);
  };

  const handleChangeFinalDate = (value, index) => {
    const newDateRanges = [...dateRanges];

    newDateRanges[index] = {
      ...newDateRanges[index],
      finalDate: value
    };
    setDateRanges(newDateRanges);
  };

  const getStartDateOfNewRange = (ubicationId) => {
    const ranges = dateRanges.filter((range) => range.ubicationId === ubicationId);
    const lastRange = ranges[ranges.length - 1];
    const dateToReturn = new Date(lastRange.finalDate || lastRange.startDate);
    const nextDate = new Date(
      dateToReturn.getFullYear(),
      dateToReturn.getUTCMonth(),
      dateToReturn.getUTCDate() + 1
    );

    return formatFullDate(nextDate, formatsEnum.FORMAT_DATE_2);
  };

  const handleAddRange = (posLineId) => {
    const newDateRanges = [
      ...dateRanges,
      {
        posLineId,
        startDate: getStartDateOfNewRange(posLineId),
        finalDate: ''
      }
    ];

    setDateRanges(newDateRanges);
  };

  const handleSuprRange = (index) => {
    const newDateRanges = dateRanges.filter((_, i) => i !== index);

    setDateRanges(newDateRanges);
  };

  const getPosLineDateRanges = (posLineId) =>
    dateRanges.filter((dateRange) => dateRange.posLineId === posLineId);

  const handleSubmitAddUbicationToPL = async () => {
    try {
      setLoader(true);
      const body = {
        idUbicacion: ubicationsSelecteds.map((ubication) => ubication.id),
        lineas: formatLinesBody(dateRanges, positionLinesSelectedsState)
      };
      const response = await postUbicationToPosLine(id, body);

      setNewToast({
        type: 'SUCCESS',
        message: response?.mensaje
      });
      setShowAddUbicationsToPLModal(false);
      onSubmit();
    } catch (err) {
      setLoader(false);
      setNewToast({
        type: 'ERROR',
        message: err.message
      });
    }
  };

  const handleSubmit = async () => {
    setDisabledPrimaryAction(true);
    handleSubmitAddUbicationToPL();

    setDisabledPrimaryAction(false);
  };

  useEffect(() => {
    const disabled = !ubicationsSelecteds.length;

    setDisabledPrimaryAction(disabled);
  }, [ubicationsSelecteds]);

  useEffect(() => {
    if (showAddUbicationsToPLModal) {
      const mappedSelectedValues = initialUbicationsSelecteds.map((ubication) => ({
        value: ubication.id,
        label: ubication.nombre
      }));
      // Al seteo de las  ubicaciones iniciales se les agrega las elegidas por el usuario
      const bothUbications = [...mappedSelectedValues, ...ubicationsSelecteds].filter(
        (ubication, index, self) => index === self.findIndex((t) => t.value === ubication.value)
      );

      setUbicationsSelecteds(bothUbications);
    }

    if (showAddUbicationsToPLModal && !ubications.length) getUbications(positionsLinesSelecteds);

    // Si cerramos el modal
    if (!showAddUbicationsToPLModal) setUbicationsSelecteds([]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionsLinesSelecteds]);

  useEffect(() => {
    if (positionLinesSelectedsState.length > 0) {
      const initialDateRanges = positionLinesSelectedsState.map((posLine) => ({
        posLineId: posLine.id,
        startDate: firstDayOfSSM,
        finalDate: lastDayOfSSM
      }));

      setDateRanges(initialDateRanges);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionLinesSelectedsState]);

  return (
    <ModalGeneric
      backdrop="static"
      infoModal
      onHide={() => setShowAddUbicationsToPLModal(false)}
      primaryAction={handleSubmit}
      primaryActionDisabled={disabledPrimaryAction}
      primaryActionLabel="Aceptar"
      secondaryAction={() => setShowAddUbicationsToPLModal(false)}
      secondaryActionLabel="Salir"
      show={showAddUbicationsToPLModal}
      size="xl"
      title="Agregar posición y línea a ubicaciones"
      variantHeader="light"
    >
      <>
        <SelectGeneric
          defaultValueOneOption={false}
          isDisabled={loading}
          isMulti
          label="Ubicación"
          name="ubicaciones"
          onChange={handleOnChangeUbication}
          options={ubications}
          paddingIndicator={4}
          placeholder="Seleccione ubicación"
          value={ubicationsSelecteds}
          variantOptions="info"
          withSelect="100%"
        />
        <hr />
        {positionLinesSelectedsState.map((positionLine) => (
          <>
            <Row key={positionLine.id}>
              <Col>
                <h5>
                  <strong>Posición y línea seleccionadas</strong>
                </h5>
                <div className="d-felx justify-content-between align-items-center">
                  <BoxesWithText backgroundColor="#3865B8" color="#FFF">
                    P{positionLine.numeroPosicion}
                  </BoxesWithText>
                  <strong className="mx-1">{positionLine.descripcionPosicionSap}</strong>
                  <BoxesWithText backgroundColor="#646464" color="#FFF">
                    L{positionLine.numeroLinea}
                  </BoxesWithText>
                  <strong className="mx-1">{positionLine.descripcionLineaSap}</strong>
                </div>
              </Col>
              <Col>
                <h5>
                  <strong>Realización de la tarea</strong>
                </h5>

                {dateRanges?.map((dateRange, index) => {
                  if (dateRange.posLineId === positionLine.id) {
                    return (
                      <DateRange
                        disableSuprButton={getPosLineDateRanges(positionLine.id).length === 1}
                        finalDate={dateRange.finalDate}
                        handleAddRange={handleAddRange}
                        handleSuprRange={handleSuprRange}
                        id={dateRange.posLineId}
                        index={index}
                        key={dateRange.startDate.toString()}
                        max={lastDayOfSSM}
                        min={index === 0 ? firstDayOfSSM : dateRange.startDate}
                        onChangeFinalDate={handleChangeFinalDate}
                        onChangeStartDate={handleChangeStartDate}
                        startDate={dateRange.startDate}
                      />
                    );
                  }

                  return null;
                })}
              </Col>
            </Row>
            <hr />
          </>
        ))}
      </>
    </ModalGeneric>
  );
};

export default AddUbicationsToPL;

AddUbicationsToPL.propTypes = {
  showAddUbicationsToPLModal: PropTypes.bool.isRequired,
  setShowAddUbicationsToPLModal: PropTypes.func.isRequired,
  idServisRequestMonthly: PropTypes.string.isRequired,
  initialUbicationsSelecteds: PropTypes.arrayOf(PropTypes.shape({})),
  positionsLinesSelecteds: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      descripcionPosicionSap: PropTypes.string.isRequired,
      descripcionLineaSap: PropTypes.string.isRequired,
      descripcionPosicionYClic: PropTypes.string.isRequired,
      descripcionLineaYClic: PropTypes.string.isRequired,
      numeroLinea: PropTypes.number.isRequired,
      numeroPosicion: PropTypes.number.isRequired,
      unidadMedida: PropTypes.string.isRequired
    })
  ),
  month: PropTypes.string.isRequired,
  year: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired
};

AddUbicationsToPL.defaultProps = {
  initialUbicationsSelecteds: [],
  positionsLinesSelecteds: []
};
